import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/** Public catalogue of membership plans. Use the server service client so the
 * public plans page does not depend on browser/session RLS or anon-key quirks. */
export const MEMBERSHIP_CATALOGUE = [
  { slug:"customer-essential", audience:"customer", name:"Shields Essential", description:"Book trusted work and keep the essentials in one place.", billing_interval:"month", price_pence_monthly:0, features:["Book and manage trusted local services","Protected GBP payment flow","Secure Partner messaging","Reviews and booking history","One saved property","Basic Shields AI"], sort_order:10, monthly_post_limit:3, unlimited_messaging:true, monthly_credits:0, portfolio_limit:null, property_limit:1, entitlement_keys:["booking","payments","messaging","reviews","booking_history","basic_ai"] },
  { slug:"customer-home", audience:"customer", name:"Shields Home", description:"Stay ahead of maintenance, records and repeat work for your home.", billing_interval:"month", price_pence_monthly:999, features:["Everything in Essential","Home Health and service history","Evidence-backed maintenance reminders","Home document vault","Fast repeat booking","Enhanced home AI"], sort_order:20, monthly_post_limit:10, unlimited_messaging:true, monthly_credits:0, portfolio_limit:null, property_limit:1, entitlement_keys:["booking","payments","messaging","reviews","booking_history","basic_ai","home_health","home_reminders","home_vault","repeat_booking","home_ai"] },
  { slug:"customer-family", audience:"customer", name:"Shields Family", description:"Manage several properties and share home organisation with your household.", billing_interval:"month", price_pence_monthly:1999, features:["Everything in Home","Up to five properties","Household sharing","Shared maintenance calendar","Shared home documents","Property spending overview"], sort_order:30, monthly_post_limit:null, unlimited_messaging:true, monthly_credits:0, portfolio_limit:null, property_limit:5, entitlement_keys:["booking","payments","messaging","reviews","booking_history","basic_ai","home_health","home_reminders","home_vault","repeat_booking","home_ai","household_sharing","shared_calendar","home_spend_insights"] },
  { slug:"customer-complete", audience:"customer", name:"Shields Complete", description:"The complete home-management experience with proactive preparation and priority support.", billing_interval:"month", price_pence_monthly:2999, features:["Everything in Family","Up to ten properties","Advanced maintenance planning","Advanced home spending insights","Priority support","Approved proactive AI preparation"], sort_order:40, monthly_post_limit:null, unlimited_messaging:true, monthly_credits:0, portfolio_limit:null, property_limit:10, entitlement_keys:["booking","payments","messaging","reviews","booking_history","basic_ai","home_health","home_reminders","home_vault","repeat_booking","home_ai","household_sharing","shared_calendar","home_spend_insights","advanced_home_planning","priority_support","proactive_ai"] },
  { slug:"partner-starter-v2", audience:"partner", name:"Shields Business Starter", description:"Get work and run the essentials without a monthly subscription.", billing_interval:"month", price_pence_monthly:0, features:["Approved Partner profile and opportunities","Quotes, bidding and Get Now","Secure customer messaging","Jobs and basic calendar","Customer records","Basic invoices"], sort_order:10, monthly_post_limit:3, unlimited_messaging:true, monthly_credits:0, portfolio_limit:12, property_limit:null, entitlement_keys:["crm","quotes","invoices","calendar","jobs","opportunities","basic_ai"] },
  { slug:"partner-pro-v2", audience:"partner", name:"Shields Business Pro", description:"Run smarter with Credits, repeat-customer benefits and professional business tools.", billing_interval:"month", price_pence_monthly:2999, features:["Everything in Starter","2,000 SC every paid month","25% repeat-customer Credits saving","Quote templates and branded invoices","Expenses and job costing","Advanced calendar and insights","Enhanced business AI","Expanded portfolio"], sort_order:20, monthly_post_limit:25, unlimited_messaging:true, monthly_credits:2000, portfolio_limit:60, property_limit:null, entitlement_keys:["crm","quotes","invoices","calendar","jobs","opportunities","basic_ai","expenses","job_costing","analytics","repeat_customer_priority_discount","enhanced_ai","quote_templates","branded_invoices","advanced_calendar"] },
  { slug:"partner-business-v2", audience:"partner", name:"Shields Business", description:"Grow a team with shared operations, CRM and deeper reporting.", billing_interval:"month", price_pence_monthly:5999, features:["Everything in Pro","5,000 SC every paid month","Up to five team members","Shared operations and customer records","Advanced CRM and follow-ups","Conversion and profitability reporting","Business automation tools"], sort_order:30, monthly_post_limit:null, unlimited_messaging:true, monthly_credits:5000, portfolio_limit:200, property_limit:null, entitlement_keys:["crm","quotes","invoices","calendar","jobs","opportunities","basic_ai","expenses","job_costing","analytics","repeat_customer_priority_discount","enhanced_ai","quote_templates","branded_invoices","advanced_calendar","teams","advanced_crm","followups","profitability","reports","business_automation"] },
  { slug:"partner-enterprise-v2", audience:"partner", name:"Shields Business Enterprise", description:"Operate at scale with larger teams, advanced controls and reporting.", billing_interval:"month", price_pence_monthly:9999, features:["Everything in Business","10,000 SC every paid month","Up to twenty team members","Manager and team permissions","Advanced reporting and exports","Multi-area operations","Priority Partner support","Expanded approved automation"], sort_order:40, monthly_post_limit:null, unlimited_messaging:true, monthly_credits:10000, portfolio_limit:500, property_limit:null, entitlement_keys:["crm","quotes","invoices","calendar","jobs","opportunities","basic_ai","expenses","job_costing","analytics","repeat_customer_priority_discount","enhanced_ai","quote_templates","branded_invoices","advanced_calendar","teams","advanced_crm","followups","profitability","reports","business_automation","team_permissions","advanced_exports","multi_area","priority_support","enterprise_automation"] },
] as const;

/** Public catalogue is code-backed as well as DB-backed so a production DB migration
 * cannot leave the new memberships invisible while deployment catches up. */
export const listMembershipPlans = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin.from("membership_plans").select("slug, audience, name, description, billing_interval, price_pence_monthly, features, sort_order, monthly_post_limit, unlimited_messaging, monthly_credits, portfolio_limit, property_limit, entitlement_keys").eq("active", true);
    if (!error && data) {
      const rows = new Map(data.map((p:any)=>[p.slug,p]));
      return MEMBERSHIP_CATALOGUE.map(plan => ({ ...plan, ...(rows.get(plan.slug) ?? {}) }));
    }
  } catch (error) { console.warn("[Memberships] DB catalogue unavailable; using release catalogue", error); }
  return MEMBERSHIP_CATALOGUE.map(plan => ({...plan}));
});

/** The signed-in user's active membership, or null (everyone defaults to free).
 * Read this server-side as well: membership plan details are catalogue data and
 * must not blank /plans because of a browser RLS or relationship permission. */
export const getMyMembership = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: membership, error: membershipError } = await supabaseAdmin
      .from("memberships")
      .select("id, plan_slug, status, started_at, current_period_end, cancel_at_period_end, stripe_status, cancelled_at")
      .eq("user_id", context.userId)
      .in("status", ["active", "past_due"])
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (membershipError) {
      console.warn("[Memberships] active membership lookup failed; using free state", membershipError);
      return null;
    }
    if (!membership) return null;

    const { data: plan, error: planError } = await supabaseAdmin
      .from("membership_plans")
      .select(
        "slug, audience, name, price_pence_monthly, features, monthly_post_limit, unlimited_messaging, monthly_credits, portfolio_limit, property_limit, entitlement_keys",
      )
      .eq("slug", membership.plan_slug)
      .eq("active", true)
      .maybeSingle();
    if (planError) console.warn("[Memberships] plan detail lookup failed", planError);
    return { ...membership, plan: plan ?? null };
  });

/** Resolved on the server from the active Stripe-controlled membership. */
export const getMyPartnerEntitlements = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: isOwner, error: roleError } = await supabaseAdmin.rpc("has_role", {
      _user_id: context.userId,
      _role: "owner",
    });
    if (roleError) console.warn("[Memberships] owner role check failed; continuing as non-owner", roleError);

    const { data: teamMembership } = await (supabaseAdmin as any)
      .from("business_team_members")
      .select("partner_id,business_role,status")
      .eq("user_id", context.userId)
      .eq("status", "active")
      .maybeSingle();
    const membershipOwnerId = teamMembership?.partner_id ?? context.userId;

    let membership: any = null;
    if (!roleError && isOwner && membershipOwnerId === context.userId) {
      const { data: testMode } = await (supabaseAdmin as any)
        .from("owner_membership_test_modes")
        .select("plan_slug")
        .eq("owner_id", context.userId)
        .eq("audience", "partner")
        .maybeSingle();
      if (testMode?.plan_slug) {
        const { data: plan } = await (supabaseAdmin as any)
          .from("membership_plans")
          .select("name, entitlement_keys")
          .eq("slug", testMode.plan_slug)
          .eq("audience", "partner")
          .eq("active", true)
          .maybeSingle();
        membership = { plan_slug: testMode.plan_slug, membership_plans: plan };
      }
    }
    if (!membership) {
      const result = await supabaseAdmin
        .from("memberships")
        .select("plan_slug, status, membership_plans(name, entitlement_keys)")
        .eq("user_id", membershipOwnerId)
        .eq("status", "active")
        .maybeSingle();
      membership = result.data;
    }
    const plan = membership?.membership_plans as { name?: string; entitlement_keys?: string[] } | null;
    return {
      planSlug: membership?.plan_slug ?? "partner-starter-v2",
      planName: plan?.name ?? "Shields Business Starter",
      keys: plan?.entitlement_keys ?? ["crm", "quotes", "invoices", "calendar", "jobs", "opportunities", "basic_ai"],
      businessOwnerId: membershipOwnerId,
      teamRole: teamMembership?.business_role ?? "owner",
    };
  });

/** Activate a £0 plan instantly. Paid plans go through createCheckoutSession (embedded Stripe Checkout). */
export const activateFreePlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ planSlug: z.string().min(2).max(60) }).parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: plan } = await supabaseAdmin
      .from("membership_plans")
      .select("slug, audience, price_pence_monthly")
      .eq("slug", data.planSlug)
      .eq("active", true)
      .maybeSingle();
    if (!plan) return { ok: false as const, error: "Plan not found" };
    if (plan.price_pence_monthly > 0) return { ok: false as const, error: "Paid plans need checkout" };
    const [{ data: partnerRole }, { data: partnerApplication }] = plan.audience === "partner"
      ? await Promise.all([
          supabaseAdmin.rpc("has_role", { _user_id: userId, _role: "partner" }),
          supabaseAdmin.from("partner_applications").select("status").eq("user_id", userId).eq("status", "approved").maybeSingle(),
        ])
      : [{ data: true }, { data: { status: "approved" } }];
    if (plan.audience === "partner" && (!partnerRole || !partnerApplication)) {
      return { ok: false as const, error: "Partner memberships are available only after Partner approval" };
    }

    const { data: currentPaid } = await supabaseAdmin
      .from("memberships")
      .select("id, plan_slug, stripe_subscription_id, membership_plans(audience, price_pence_monthly)")
      .eq("user_id", userId)
      .eq("status", "active")
      .not("stripe_subscription_id", "is", null)
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const currentPlan = currentPaid?.membership_plans as { audience?: string; price_pence_monthly?: number } | null;
    if (
      currentPaid?.stripe_subscription_id &&
      currentPlan?.price_pence_monthly &&
      currentPlan.audience === plan.audience
    ) {
      return {
        ok: false as const,
        error: "Manage or cancel your current paid subscription before moving to the free plan.",
        manageSubscription: true as const,
      };
    }

    await supabaseAdmin
      .from("memberships")
      .update({ status: "cancelled", updated_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("status", "active")
      .is("stripe_subscription_id", null);
    const { error } = await supabaseAdmin.from("memberships").insert({
      user_id: userId,
      plan_slug: plan.slug,
      status: "active",
    });
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });
